import { useDisclosure } from "@chakra-ui/react"
import { useEffect } from "react"
import {
  IntercomCustomEvent,
  IntercomUpdateOptions,
  useIntercomContext
} from "./IntercomProvider"

export function usePathwrightIntercomLauncher(options?: IntercomUpdateOptions) {
  const { usingPathwrightIntercom } = useIntercomContext()
  const disclosure = useIntercomLauncher({
    ...options,
    enabled: usingPathwrightIntercom
  })
  return {
    ...disclosure,
    usingPathwrightIntercom
  }
}

const defaultIntercomOptions: IntercomUpdateOptions = {
  horizontal_padding: 20, // Horizontal padding
  vertical_padding: 20, // Vertical padding
  hide_default_launcher: false
}

// TODO: Consider using a selector-based approach instead of state-based.
// Had started down that path but wasn't working. The benefit might be that
// Intercom would handle positioning the messenger window relative to the
// selector element.

// Allows for launching Intercom from a custom launcher button.
export function useIntercomLauncher(
  options?: IntercomUpdateOptions & { enabled: boolean }
) {
  const disclosure = useDisclosure()
  const { open, onOpen, onClose } = disclosure
  const { enabled = true, ...intercomOptions } = options || {}

  useEffect(() => {
    if (window.Intercom) {
      // Update Intercom with the options passed in.
      window.Intercom("update", {
        ...intercomOptions,
        hide_default_launcher: true
      })
    }
  }, [options])

  useEffect(() => {
    return () => {
      if (window.Intercom) {
        // Restored default Intercom options passed on unmount.
        window.Intercom("update", defaultIntercomOptions)
      }
    }
  }, [])

  useEffect(() => {
    if (window.Intercom) {
      if (open) {
        window.Intercom("showNewMessage")
      } else {
        window.Intercom("hide")
      }
    }
  }, [open])

  useEffect(() => {
    if (enabled) {
      const handleToggle = () => {
        window.Intercom("onShow", () => onOpen())
        window.Intercom("onHide", () => onClose())
      }

      // Setup initial handlers for onShow and onHide events.
      handleToggle()

      // Bind "boot" handler since when Intercom("shutdown") is executed,
      // any previous onShow and onHide handlers are removed and will need to
      // be bound again after Intercom("boot").
      const handler = (e: IntercomCustomEvent) => {
        if (e.detail === "boot") handleToggle()
      }
      window.addEventListener("intercom", handler)
      return () => window.removeEventListener("intercom", handler)
    }
  }, [enabled])

  return disclosure
}

export default useIntercomLauncher
